export class AnalyticsBiApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** The metrics and dimensions the caller may use (the personal dimensions only with `analytics:view_personal`). */
    catalog() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/catalog', auth: true });
    }
    /** Run a governed query. */
    query(request) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/query', body: request, auth: true });
    }
    /** Export a query as CSV text (UTF-8 with a byte-order mark; `;` form on request). Needs `analytics:export`; at most the server's row cap. */
    exportCsv(request, options = {}) {
        return this.client.request({
            method: 'POST',
            path: '/v1/analytics/export',
            body: options.delimiter !== undefined ? { ...request, delimiter: options.delimiter } : request,
            auth: true,
            expect: 'text',
        });
    }
    // ---- the operator's own saved reports (D523): only the owner sees or changes one; anyone else's id is a 404 ----
    /** The caller's saved reports, the most recently changed first (`analytics:view`). */
    reports() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/reports', auth: true });
    }
    /** Save a report (`analytics:save_report`). 409 `REPORT_NAME_TAKEN` / `REPORT_LIMIT_REACHED`; 403 `ANALYTICS_PERSONAL_FORBIDDEN` for a personal query without the word. */
    saveReport(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/reports', body: input, auth: true });
    }
    /** Rename a report and/or replace its query. */
    updateReport(id, patch) {
        return this.client.request({ method: 'PATCH', path: `/v1/analytics/reports/${encodeURIComponent(id)}`, body: patch, auth: true });
    }
    /** Delete a report. */
    deleteReport(id) {
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/reports/${encodeURIComponent(id)}`, auth: true });
    }
    // ---- D528: filters v2 ----
    /** The distinct values of a dimension, within the operator's reach, with counts (`analytics:view`; a person needs `analytics:view_personal`). */
    values(input) {
        const params = new URLSearchParams({ dimension: input.dimension });
        if (input.search !== undefined && input.search !== '')
            params.set('search', input.search);
        if (input.limit !== undefined)
            params.set('limit', String(input.limit));
        return this.client.request({ method: 'GET', path: `/v1/analytics/values?${params.toString()}`, auth: true });
    }
    /** Open a shared state: the server strips personal values, validates and re-authorizes (403 for a market outside the reach), and runs nothing. */
    checkFilters(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/filters/check', body: input, auth: true });
    }
    /** The caller's own filter sets and the team's shared ones the caller's reach covers. */
    filterSets() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/filter-sets', auth: true });
    }
    /** Keep a filter set (`analytics:save_report`). 409 `FILTER_SET_NAME_TAKEN` / `FILTER_SET_LIMIT_REACHED`; 400 `FILTER_SET_PERSONAL_VALUES_FORBIDDEN`. */
    saveFilterSet(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/filter-sets', body: input, auth: true });
    }
    /** Rename a set, replace its state and/or share it. Only the owner; anyone else's id is a 404. */
    updateFilterSet(id, patch) {
        return this.client.request({ method: 'PATCH', path: `/v1/analytics/filter-sets/${encodeURIComponent(id)}`, body: patch, auth: true });
    }
    deleteFilterSet(id) {
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/filter-sets/${encodeURIComponent(id)}`, auth: true });
    }
    // ---- D532: the asynchronous export. Jobs run in the background (the worker); the file is downloaded when it is done. ----
    /** Queue an export (`analytics:export`). 202 with the job. 409 `EXPORT_LIMIT_REACHED`; 400/403 as the planner and the reach decide. */
    enqueueExport(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/export-jobs', body: input, auth: true });
    }
    /** The caller's jobs (newest first) and the files delivered to the caller. */
    exportJobs() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/export-jobs', auth: true });
    }
    /** One of the caller's jobs (status, progress); anyone else's id is a 404. */
    exportJob(id) {
        return this.client.request({ method: 'GET', path: `/v1/analytics/export-jobs/${encodeURIComponent(id)}`, auth: true });
    }
    /** Cancel a queued or running job, or delete a finished one and its file. */
    removeExport(id) {
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/export-jobs/${encodeURIComponent(id)}`, auth: true });
    }
    /**
     * The file of a finished job. The server asks the grants again NOW (403 if the owner lost a market or the personal word), writes the access-log row of a
     * personal file first (503 `ANALYTICS_ACCESS_LOG_UNAVAILABLE` withholds it), and answers 409 `ANALYTICS_EXPORT_NOT_READY` / `ANALYTICS_EXPORT_EXPIRED`.
     */
    async downloadExport(id) {
        const res = await this.client.request({ method: 'GET', path: `/v1/analytics/export-jobs/${encodeURIComponent(id)}/download`, auth: true, expect: 'bytes', timeoutMs: 300_000 });
        if (!res.ok)
            return res;
        const disposition = res.value.headers['content-disposition'] ?? '';
        const named = /filename="([^"]+)"/.exec(disposition)?.[1];
        return { ok: true, value: { bytes: res.value.bytes, fileName: named ?? 'export', contentType: res.value.headers['content-type'] ?? 'application/octet-stream' } };
    }
    /** The columns an export of this source would have (derived from the plan), and the choices a template carries. Queues nothing. */
    exportColumns(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/export-columns', body: input, auth: true });
    }
    /** The operators a schedule may deliver to (names and ids only). */
    exportRecipients() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/export-recipients', auth: true });
    }
    exportSchedules() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/export-schedules', auth: true });
    }
    /** Keep a schedule (`analytics:export`). 400 `SCHEDULE_RECIPIENT_*` for a recipient who is not an operator who can read what it reads; 409 `SCHEDULE_NAME_TAKEN` / `SCHEDULE_LIMIT_REACHED`. */
    saveExportSchedule(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/export-schedules', body: input, auth: true });
    }
    updateExportSchedule(id, patch) {
        return this.client.request({ method: 'PATCH', path: `/v1/analytics/export-schedules/${encodeURIComponent(id)}`, body: patch, auth: true });
    }
    deleteExportSchedule(id) {
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/export-schedules/${encodeURIComponent(id)}`, auth: true });
    }
    /** The caller's own templates and the team's shared ones the caller's reach covers. */
    exportTemplates() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/export-templates', auth: true });
    }
    saveExportTemplate(input) {
        return this.client.request({ method: 'POST', path: '/v1/analytics/export-templates', body: input, auth: true });
    }
    updateExportTemplate(id, patch) {
        return this.client.request({ method: 'PATCH', path: `/v1/analytics/export-templates/${encodeURIComponent(id)}`, body: patch, auth: true });
    }
    deleteExportTemplate(id) {
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/export-templates/${encodeURIComponent(id)}`, auth: true });
    }
    /** D531: the goals of the markets the caller's `analytics:view` reaches. */
    targets() {
        return this.client.request({ method: 'GET', path: '/v1/analytics/targets', auth: true });
    }
    /** D531: set a goal (`analytics:manage_targets`, inside the reach of that word and of `analytics:view`). 201 when new, 200 when replaced. */
    setTarget(input) {
        return this.client.request({ method: 'PUT', path: '/v1/analytics/targets', body: input, auth: true });
    }
    /** D531: clear a goal. 404 `ANALYTICS_TARGET_NOT_FOUND` when there is none. */
    clearTarget(metric, marketId) {
        const params = new URLSearchParams({ metric, marketId });
        return this.client.request({ method: 'DELETE', path: `/v1/analytics/targets?${params.toString()}`, auth: true });
    }
}
//# sourceMappingURL=analytics-bi.js.map